import type { Location } from 'react-router-dom';

import type { UniverseReference } from '@wecasa/common/src/types/commons';
import { isChildcareReference } from '@wecasa/common/src/utils/universes';

import { translate } from '@wecasa/i18n';

const t = translate('screens');

type CommonPath =
  | 'root'
  | 'test_a'
  | 'test_b'
  | 'previous_pros'
  | 'prestations'
  | 'customer'
  | 'recap'
  | 'checkout_confirmation';

type SignInPath = 'sign_in' | 'signup_choice' | 'signup';

type ChildcarePath = 'appointment_spot' | 'children' | 'activities' | 'allowance' | 'summary';

type WellnessPath = 'appointment_calendar' | 'appointment_day' | 'appointment_hour' | 'categories';

type CleaningPath =
  | 'animals'
  | 'duration'
  | 'frequency'
  | 'instant_tax_credit_selection'
  | 'modify_order'
  | 'modify_potential'
  | 'options'
  | 'services';

export type Path = CommonPath | SignInPath | ChildcarePath | WellnessPath | CleaningPath;

// Common
export const TEST_A_PATH = 'test_a';
export const TEST_B_PATH = 'test_b';
export const PREVIOUS_PROS_PATH = 'previous_pros';
export const PRESTATIONS_PATH = 'prestations';
export const CUSTOMER_PATH = 'customer';
export const RECAP_PATH = 'recap';
export const CHECKOUT_CONFIRMATION_PATH = 'checkout_confirmation';

// SignIn
export const SIGN_IN_PATH = 'sign_in';
export const SIGNUP_CHOICE_PATH = 'signup_choice';
export const SIGNUP_PATH = 'signup';

// Childcare
export const ACTIVITIES_PATH = 'activities';
export const ALLOWANCE_PATH = 'allowance';
export const CHILDREN_PATH = 'children';
export const SPOT_APPOINTMENT_PATH = 'appointment_spot';
export const SUMMARY_PATH = 'summary';

// Wellness
export const CATEGORIES_PATH = 'categories';
export const APPOINTMENT_DAY_PATH = 'appointment_day';
export const APPOINTMENT_HOUR_PATH = 'appointment_hour';
export const APPOINTMENT_CALENDAR_PATH = 'appointment_calendar';

// Cleaning
export const ANIMALS_PATH = 'animals';
export const CLEANING_OPTIONS_PATH = 'options';
export const DURATION_PATH = 'duration';
export const FREQUENCY_PATH = 'frequency';
export const INSTANT_TAX_CREDIT_SELECTION_PATH = 'instant_tax_credit_selection';
export const MODIFY_ORDER_PATH = 'modify_order';
export const MODIFY_POTENTIAL_PATH = 'modify_potential';
export const SERVICES_PATH = 'services';

type LocalizedPaths = Record<Path, string>;

export const ROUTES: LocalizedPaths = {
  root: '/',
  [ACTIVITIES_PATH]: '/activities',
  [ALLOWANCE_PATH]: '/allowance',
  [ANIMALS_PATH]: '/animals',
  [APPOINTMENT_CALENDAR_PATH]: '/session/calendar',
  [APPOINTMENT_DAY_PATH]: '/session/day',
  [APPOINTMENT_HOUR_PATH]: '/session/hour',
  [CATEGORIES_PATH]: '/categories',
  // booking_uuid is a path segment: private browsing modes scrub query params on a cross-site return.
  [CHECKOUT_CONFIRMATION_PATH]: '/intent/:intentType/:intentId/booking/:bookingUuid/confirmation',
  [CHILDREN_PATH]: '/children',
  [CLEANING_OPTIONS_PATH]: '/options',
  [CUSTOMER_PATH]: '/customer',
  [DURATION_PATH]: '/duration',
  [FREQUENCY_PATH]: '/scheduling',
  [INSTANT_TAX_CREDIT_SELECTION_PATH]: '/instant-tax-credit-selection',
  [MODIFY_ORDER_PATH]: '/appointments/:id',
  [MODIFY_POTENTIAL_PATH]: '/orders/:id',
  // TODO: Refactor by using '/services/:category' and adapting screen logic & tracking
  [PRESTATIONS_PATH]: '/services',
  [PREVIOUS_PROS_PATH]: '/pros',
  [RECAP_PATH]: '/recap',
  [SERVICES_PATH]: '/services',
  [SIGN_IN_PATH]: '/login',
  [SIGNUP_CHOICE_PATH]: '/signup/option',
  [SIGNUP_PATH]: '/signup/account',
  [SPOT_APPOINTMENT_PATH]: '/session-spot',
  [SUMMARY_PATH]: '/summary',
  [TEST_A_PATH]: '/a',
  [TEST_B_PATH]: '/b',
};

type PathConfig = {
  path: Path;
  previousPath?: Path;
  shouldHideSideBasket?: boolean;
  withURLParams?: boolean;
};

const locationScreen: PathConfig = {
  path: 'root',
};

const previousProsScreen: PathConfig = {
  path: PREVIOUS_PROS_PATH,
};

const appointmentCalendarScreen = (previousPath: Path): PathConfig => ({
  path: APPOINTMENT_CALENDAR_PATH,
  previousPath,
});

const customerScreen: PathConfig = { path: CUSTOMER_PATH };

const recapScreen: PathConfig = { path: RECAP_PATH };
const checkoutConfirmationScreen: PathConfig = {
  path: CHECKOUT_CONFIRMATION_PATH,
  shouldHideSideBasket: true,
  withURLParams: true,
};

const commonScreens: PathConfig[] = [
  { path: SIGN_IN_PATH },
  { path: SIGNUP_CHOICE_PATH },
  { path: SIGNUP_PATH, previousPath: SIGNUP_PATH },
];

const wellnessScreens: PathConfig[] = [
  locationScreen,
  { path: CATEGORIES_PATH, previousPath: 'root' },
  { path: PRESTATIONS_PATH, previousPath: CATEGORIES_PATH, withURLParams: true },
  previousProsScreen,
  { path: APPOINTMENT_DAY_PATH },
  appointmentCalendarScreen(APPOINTMENT_DAY_PATH),
  { path: APPOINTMENT_HOUR_PATH },
  customerScreen,
  recapScreen,
  checkoutConfirmationScreen,
];

const SCREENS: Record<UniverseReference, PathConfig[]> = {
  childcare: [
    { ...locationScreen, shouldHideSideBasket: true },
    {
      ...previousProsScreen,
      shouldHideSideBasket: true,
    },
    {
      path: CHILDREN_PATH,
      previousPath: 'root',
      shouldHideSideBasket: true,
    },
    {
      path: PRESTATIONS_PATH,
      shouldHideSideBasket: true,
    },
    {
      path: SPOT_APPOINTMENT_PATH,
      previousPath: PRESTATIONS_PATH,
    },
    {
      path: ACTIVITIES_PATH,
    },
    customerScreen,
    {
      path: ALLOWANCE_PATH,
    },
    recapScreen,
    checkoutConfirmationScreen,
    {
      path: SUMMARY_PATH,
      shouldHideSideBasket: true,
    },
  ],
  massage: wellnessScreens,
  beauty: wellnessScreens,
  haircut: wellnessScreens,
  sports_coaching: wellnessScreens,
  cleaning: [
    { ...locationScreen, shouldHideSideBasket: true },
    {
      path: INSTANT_TAX_CREDIT_SELECTION_PATH,
      previousPath: 'root',
      shouldHideSideBasket: true,
    },
    {
      path: SERVICES_PATH,
      previousPath: 'root',
    },
    {
      path: FREQUENCY_PATH,
      // is previousPath still used ? I thought we were only using "Retour"
      previousPath: INSTANT_TAX_CREDIT_SELECTION_PATH, // depends here if the screen is used or not
    },
    {
      path: DURATION_PATH,
      previousPath: FREQUENCY_PATH,
    },
    {
      path: CLEANING_OPTIONS_PATH,
      previousPath: DURATION_PATH,
    },
    {
      path: ANIMALS_PATH,
      previousPath: CLEANING_OPTIONS_PATH,
    },
    previousProsScreen,
    {
      path: APPOINTMENT_CALENDAR_PATH,
    },
    {
      path: APPOINTMENT_HOUR_PATH,
    },
    customerScreen,
    recapScreen,
    checkoutConfirmationScreen,
  ],
};

export const route = (path: Path) => ROUTES[path];

const getScreen = (universe: UniverseReference, path: string) => {
  const screens = [...SCREENS[universe], ...commonScreens];

  // This will check if the URL starts with `/intent`, might need a refactoring later
  if (path.startsWith(`/${route('checkout_confirmation').split('/')[1]}`)) {
    // `|| screens[0]` is just for TS so it always return a screen
    return screens.find(({ path: screenPath }) => screenPath === 'checkout_confirmation') || screens[0];
  }

  return (
    screens.find(({ path: screenPath, withURLParams }) =>
      withURLParams ? path.includes(route(screenPath)) : route(screenPath) === path,
    ) || screens[0]
  );
};

export const getBackTitle = (universe: UniverseReference, { pathname }: Location) => {
  // Special case for recurring childcare, we do not show the back title on the summary path as the
  if (isChildcareReference(universe) && pathname === route(SUMMARY_PATH)) return undefined;
  return t('.main_container.back_title');
};

export const shouldHideBasket = (universe: UniverseReference, path: string) =>
  !!getScreen(universe, path).shouldHideSideBasket;
